/**
 * User-facing strings for the pending free-text reference affordance.
 * Import from here to keep messaging consistent across the wizard input,
 * post-publish toast, and bubble summary.
 */

export const PENDING_REFERENCE_TITLE = 'Pending reference';

/**
 * Heading for the grouped "finding" section shared by the statement sheet
 * and the bottom-of-bubble footnote list — pending references the expert
 * described but Primum hasn't attached yet. Separating these from the
 * already-attached links lets both surfaces be louder about what's still
 * in progress. Pluralizes on count so the lone-reference case doesn't read
 * awkwardly.
 */
export function formatFindingSectionTitle(count: number): string {
  return count === 1 ? 'Primum is finding this' : 'Primum is finding these';
}

/**
 * Note under the "finding" section heading. Carries the two reassurances
 * the sprint demo asked for — that the source appears once found, and
 * that it stays author-only until then — at the section level so it
 * isn't repeated on every row.
 */
export const PENDING_REFERENCE_SECTION_NOTE =
  "They'll appear here once found — only you can see them until then.";

/** Pre-submit wizard input note. Future tense — request not yet sent. */
export const PENDING_REFERENCE_INPUT_NOTE = 'Primum will find the source.';

/** Post-publish toast body. Present tense + visibility reassurance. */
export const PENDING_REFERENCE_TOAST_NOTE = 'Primum is finding the source. Visible only to you.';

/**
 * Post-publish confirmation toast for the provide flow, built from the two
 * outcomes a single publish can produce: references the expert pasted as a
 * URL (attached and visible immediately) versus studies they only described,
 * which Primum still has to find. The flow used to report both as "you added
 * N references"; the sprint demo flagged that as confusing for experts who
 * named a paper without a link — nothing was attached yet, so they were left
 * wondering what happened. This splits the copy so the described case leads
 * with the finding state and reassures it'll surface (author-only) once found.
 * Returns the toast title plus an optional supporting message.
 */
export function formatProvidePublishToast(
  attachedCount: number,
  findingCount: number
): { title: string; message?: string } {
  if (attachedCount === 0 && findingCount > 0) {
    const isSingle = findingCount === 1;

    return {
      title: isSingle ? 'Primum is finding your reference' : 'Primum is finding your references',
      message: isSingle
        ? "We'll add it once found — only you can see it until then."
        : "We'll add them once found — only you can see them until then.",
    };
  }

  if (findingCount === 0) {
    return {
      title: `You added ${attachedCount} ${attachedCount === 1 ? 'reference' : 'references'}`,
    };
  }

  return {
    title: `${attachedCount} ${attachedCount === 1 ? 'reference' : 'references'} added`,
    message: `Primum is finding ${findingCount} more — visible only to you until ready.`,
  };
}

/**
 * Summary line shown below the References pill in
 * `AddReferencesPromptBubble`. Returns `null` when there are no
 * outstanding help requests so callers can gate rendering without a
 * separate check.
 */
export function formatPendingHelpRequestSummary(count: number): string | null {
  if (count === 1) {
    return 'Primum is finding references to help support 1 statement.';
  }

  if (count > 1) {
    return `Primum is finding references to help support ${count} statements.`;
  }

  return null;
}
